import React, { useState, useMemo } from 'react';
import { Table, Tag, Select, Row, Col, Card, DatePicker, Empty, Typography, Space, Descriptions } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useResponsive } from '../../hooks/useResponsive';
import { useRestaurantTimeZone } from '../../hooks/useTimeZone';
import { useGetInventoryMovementsQuery, useGetInventoryItemsQuery } from '../../services/inventoryApi';
import type { InventoryMovement, InventoryMovementType } from '../../types/inventory';
import { formatStock } from '../../utils/unitHelpers';
import { formatUnitCost } from '../../utils/currencyHelpers';

const { Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const typeLabels: Record<InventoryMovementType, string> = {
  Purchase: 'Compra',
  Sale: 'Venta',
  Adjustment: 'Ajuste',
  Waste: 'Merma',
  Initial: 'Inicial',
  InternalConsumption: 'Consumo interno',
};

const typeColors: Record<InventoryMovementType, string> = {
  Purchase: 'green',
  Sale: 'blue',
  Adjustment: 'orange',
  Waste: 'red',
  Initial: 'default',
  InternalConsumption: 'purple',
};

const MovementsTab: React.FC = () => {
  const { isMobile } = useResponsive();
  const { formatInTimeZone } = useRestaurantTimeZone();

  const [selectedItemId, setSelectedItemId] = useState<string | undefined>(undefined);
  const [selectedType, setSelectedType] = useState<string | undefined>(undefined);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);

  const queryParams = useMemo(() => {
    const params: { itemId?: string; type?: string; from?: string; to?: string } = {};
    if (selectedItemId) params.itemId = selectedItemId;
    if (selectedType) params.type = selectedType;
    if (dateRange?.[0]) params.from = dateRange[0].toISOString();
    if (dateRange?.[1]) params.to = dateRange[1].toISOString();
    return params;
  }, [selectedItemId, selectedType, dateRange]);

  const { data: movements, isLoading } = useGetInventoryMovementsQuery(queryParams);
  const { data: inventoryItems } = useGetInventoryItemsQuery();

  const sortedMovements = useMemo(() => {
    if (!movements) return [];
    return [...movements].sort((a, b) => dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf());
  }, [movements]);

  const columns: ColumnsType<InventoryMovement> = [
    {
      title: 'Fecha',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 140,
      render: (date: string) => (
        <Text style={{ fontSize: '12px' }}>{formatInTimeZone(date, 'DD/MM/YYYY HH:mm')}</Text>
      ),
    },
    {
      title: 'Artículo',
      dataIndex: 'itemName',
      key: 'itemName',
      width: 150,
      render: (text: string) => <Text strong style={{ fontSize: '13px' }}>{text}</Text>,
    },
    {
      title: 'Tipo',
      dataIndex: 'type',
      key: 'type',
      width: 100,
      render: (type: string) => (
        <Tag color={typeColors[type as InventoryMovementType] || 'default'}>
          {typeLabels[type as InventoryMovementType] || type}
        </Tag>
      ),
    },
    {
      title: 'Cantidad',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 90,
      align: 'right' as const,
      render: (quantity: number) => (
        <Text strong style={{ color: quantity >= 0 ? 'var(--color-success-text)' : 'var(--color-error-text)', fontSize: '13px' }}>
          {quantity >= 0 ? '+' : ''}{formatStock(quantity, 6)}
        </Text>
      ),
    },
    {
      title: 'Stock Anterior',
      dataIndex: 'previousStock',
      key: 'previousStock',
      width: 110,
      align: 'right' as const,
      render: (value: number) => <Text style={{ fontSize: '12px' }}>{formatStock(value, 6)}</Text>,
    },
    {
      title: 'Stock Nuevo',
      dataIndex: 'newStock',
      key: 'newStock',
      width: 100,
      align: 'right' as const,
      render: (value: number) => <Text style={{ fontSize: '12px' }}>{formatStock(value, 6)}</Text>,
    },
    {
      title: 'Costo',
      dataIndex: 'unitCost',
      key: 'unitCost',
      width: 90,
      align: 'right' as const,
      render: (cost: number) => <Text style={{ fontSize: '12px' }}>{formatUnitCost(cost)}</Text>,
    },
    {
      title: 'Referencia',
      key: 'reference',
      width: 120,
      render: (_: unknown, record: InventoryMovement) => (
        <Text style={{ fontSize: '12px' }}>
          {record.referenceType || '-'}
        </Text>
      ),
    },
    {
      title: 'Usuario',
      dataIndex: 'createdByUserName',
      key: 'createdByUserName',
      width: 120,
      render: (text: string) => <Text style={{ fontSize: '12px' }}>{text}</Text>,
    },
    {
      title: 'Motivo',
      dataIndex: 'reason',
      key: 'reason',
      width: 120,
      ellipsis: true,
      render: (text: string | undefined) => (
        <Text style={{ fontSize: '12px' }}>{text || '-'}</Text>
      ),
    },
    {
      title: 'Notas',
      dataIndex: 'notes',
      key: 'notes',
      width: 150,
      ellipsis: true,
      render: (text: string | undefined) => (
        <Text style={{ fontSize: '12px' }}>{text || '-'}</Text>
      ),
    },
  ];

  const renderMobileCard = (record: InventoryMovement) => (
    <Card key={record.id} style={{ marginBottom: 12 }} size="small">
      <Space direction="vertical" style={{ width: '100%' }} size="small">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text strong>{record.itemName}</Text>
          <Tag color={typeColors[record.type as InventoryMovementType] || 'default'}>
            {typeLabels[record.type as InventoryMovementType] || record.type}
          </Tag>
        </div>

        <Descriptions size="small" column={2} bordered>
          <Descriptions.Item label="Fecha" span={2}>
            {formatInTimeZone(record.createdAt, 'DD/MM/YYYY HH:mm')}
          </Descriptions.Item>
          <Descriptions.Item label="Cantidad">
            <Text strong style={{ color: record.quantity >= 0 ? 'var(--color-success-text)' : 'var(--color-error-text)' }}>
              {record.quantity >= 0 ? '+' : ''}{formatStock(record.quantity, 6)}
            </Text>
          </Descriptions.Item>
          <Descriptions.Item label="Costo">
            {formatUnitCost(record.unitCost)}
          </Descriptions.Item>
          <Descriptions.Item label="Stock Anterior">
            {formatStock(record.previousStock, 6)}
          </Descriptions.Item>
          <Descriptions.Item label="Stock Nuevo">
            {formatStock(record.newStock, 6)}
          </Descriptions.Item>
          <Descriptions.Item label="Referencia">
            {record.referenceType || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Usuario">
            {record.createdByUserName}
          </Descriptions.Item>
          {record.reason && (
            <Descriptions.Item label="Motivo" span={2}>
              {record.reason}
            </Descriptions.Item>
          )}
          {record.notes && (
            <Descriptions.Item label="Notas" span={2}>
              {record.notes}
            </Descriptions.Item>
          )}
        </Descriptions>
      </Space>
    </Card>
  );

  return (
    <div style={{ padding: isMobile ? 0 : '16px 0' }}>
      {/* Filters */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={8} md={6}>
          <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: 4 }}>
            Artículo:
          </Text>
          <Select
            value={selectedItemId}
            onChange={(value) => setSelectedItemId(value || undefined)}
            placeholder="Todos los artículos"
            allowClear
            showSearch
            optionFilterProp="children"
            style={{ width: '100%' }}
            size={isMobile ? 'middle' : 'middle'}
          >
            {(inventoryItems || []).filter((item) => !item.isPreparation).map((item) => (
              <Option key={item.id} value={item.id}>
                {item.name}
              </Option>
            ))}
          </Select>
        </Col>

        <Col xs={12} sm={6} md={4}>
          <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: 4 }}>
            Tipo:
          </Text>
          <Select
            value={selectedType}
            onChange={(value) => setSelectedType(value || undefined)}
            placeholder="Todos"
            allowClear
            style={{ width: '100%' }}
            size={isMobile ? 'middle' : 'middle'}
          >
            <Option value="Purchase">Compra</Option>
            <Option value="Sale">Venta</Option>
            <Option value="Adjustment">Ajuste</Option>
            <Option value="Waste">Merma</Option>
            <Option value="Initial">Inicial</Option>
            <Option value="InternalConsumption">Consumo interno</Option>
          </Select>
        </Col>

        <Col xs={24} sm={10} md={8}>
          <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: 4 }}>
            Periodo:
          </Text>
          <RangePicker
            value={dateRange}
            onChange={(dates) => setDateRange(dates)}
            style={{ width: '100%' }}
            size={isMobile ? 'middle' : 'middle'}
            format="DD/MM/YYYY"
          />
        </Col>
      </Row>

      {/* Table or Cards */}
      {isMobile ? (
        isLoading ? (
          <div style={{ textAlign: 'center', padding: 20 }}>Cargando...</div>
        ) : sortedMovements.length > 0 ? (
          <div style={{ maxHeight: 'calc(100vh - 350px)', overflowY: 'auto' }}>
            {sortedMovements.map((record) => renderMobileCard(record))}
          </div>
        ) : (
          <Empty
            description="No hay movimientos de inventario"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ padding: 40 }}
          />
        )
      ) : (
        <Table
          columns={columns}
          dataSource={sortedMovements}
          loading={isLoading}
          rowKey="id"
          size="middle"
          scroll={{ x: 1200 }}
          pagination={{
            pageSize: 15,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} movimientos`,
          }}
          locale={{
            emptyText: (
              <Empty
                description="No hay movimientos de inventario"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
        />
      )}
    </div>
  );
};

export default MovementsTab;
